import { StandardEvents } from '@shopify/events';

const root = document.documentElement;
const loader = document.querySelector('[data-daaman-loader]');
const cartButtons = () => document.querySelectorAll('[data-daaman-mobile-cart]');
const cartTriggers = () => document.querySelectorAll('[data-daaman-cart-label]');
const cartBadges = () => document.querySelectorAll('[data-daaman-cart-count]');

let fallbackTimer;
let drawerObserver;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function hideLoader() {
  root.classList.add('daaman-loaded');
  loader?.setAttribute('aria-hidden', 'true');
  window.clearTimeout(fallbackTimer);
}

function showLoader() {
  if (!loader) return;
  loader.setAttribute('aria-hidden', 'false');
  root.classList.remove('daaman-loaded');

  window.clearTimeout(fallbackTimer);
  fallbackTimer = window.setTimeout(hideLoader, prefersReducedMotion() ? 500 : 2400);
}

function hideInitialLoader() {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(hideLoader);
  });
}

function isPlainSameSiteNavigation(event, link) {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (link.target && link.target !== '_self') return false;
  if (link.hasAttribute('download')) return false;

  const href = link.getAttribute('href');
  if (!href || href === '#') return false;
  if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;

  const nextUrl = new URL(link.href, window.location.href);
  if (nextUrl.origin !== window.location.origin) return false;

  const samePath = nextUrl.pathname === window.location.pathname && nextUrl.search === window.location.search;
  return !(samePath && nextUrl.hash);
}

function syncCartCount(itemCount) {
  const count = Number.isFinite(itemCount) ? itemCount : 0;
  const itemLabel = count === 1 ? '1 item' : `${count} items`;

  cartBadges().forEach((badge) => {
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.classList.toggle('visually-hidden', count === 0);
    badge.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
  });

  cartTriggers().forEach((trigger) => {
    const label = trigger.getAttribute('data-daaman-cart-label') || 'Cart';
    trigger.setAttribute('aria-label', count > 0 ? `${label}, ${itemLabel}` : label);
  });
}

function syncCartDrawerState() {
  const cartDrawer = document.getElementById('cart-drawer');
  const isOpen = cartDrawer?.hasAttribute('open') ?? false;

  cartButtons().forEach((button) => {
    button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
}

function watchCartDrawer() {
  const cartDrawer = document.getElementById('cart-drawer');
  if (!cartDrawer) return;
  if (drawerObserver) return;

  syncCartDrawerState();

  drawerObserver = new MutationObserver(syncCartDrawerState);
  drawerObserver.observe(cartDrawer, {
    attributes: true,
    attributeFilter: ['open'],
  });
}

function restoreCachedCartCount() {
  try {
    const stored = JSON.parse(sessionStorage.getItem('cart-count') ?? '{}');
    const value = Number.parseInt(stored.value, 10);
    if (Number.isFinite(value)) syncCartCount(value);
  } catch {
    // Ignore malformed session data from older theme sessions.
  }
}

function syncRenderedCartCount() {
  const firstBadge = document.querySelector('[data-daaman-cart-count]');
  const count = Number.parseInt(firstBadge?.textContent ?? '0', 10);
  if (Number.isFinite(count)) syncCartCount(count);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hideInitialLoader, { once: true });
} else {
  hideInitialLoader();
}

window.addEventListener('pageshow', () => {
  hideInitialLoader();
  syncRenderedCartCount();
  restoreCachedCartCount();
  watchCartDrawer();
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[href]');
    if (!link || !isPlainSameSiteNavigation(event, link)) return;

    showLoader();
  },
  { capture: true }
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;
      syncCartCount(itemCount);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[daaman-mobile-shell] Cart update failed:', error);
    });
});

syncRenderedCartCount();
watchCartDrawer();
